import type { Dispatch, FC, FormEventHandler, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

export interface ManagerLeaveRequest {
  id: number;
  employee_name: string;
  leave_type_name: string;
  start_date: string;
  end_date: string;
  status: string;
}

export type LeaveDecision = 'approve' | 'reject';

export interface ManagerTeamSummary {
  headcount: number;
  pending_leave_requests: number;
  pending_timesheets: number;
  open_clock_sessions: number;
  approved_timesheets_30d: number;
  active_performance_goals?: number;
  submitted_performance_reviews?: number;
  pending_compensation_adjustments?: number;
}

export interface ManagerTimesheet {
  id: number;
  employee_name: string;
  employee_code: string;
  department?: string | null;
  work_date: string;
  clock_in?: string | null;
  clock_out?: string | null;
  regular_hours: number;
  overtime_hours: number;
  status: string;
}

export interface ManagerEmployee {
  id: number | string;
  full_name: string;
  employee_code: string;
  department?: string | null;
  position?: string | null;
}

export interface PerformanceCycle {
  id: number | string;
  name: string;
  period_start: string;
  period_end: string;
  status: string;
}

export interface ManagerPerformanceGoal {
  id: number | string;
  employee_id: number | string;
  employee_name: string;
  employee_code: string;
  cycle_id?: number | string | null;
  cycle_name?: string | null;
  title: string;
  metric?: string | null;
  target_value?: string | null;
  weight: number;
  progress: number;
  status: string;
  due_date?: string | null;
}

export interface ManagerPerformanceReview {
  id: number | string;
  employee_name: string;
  employee_code: string;
  cycle_name: string;
  overall_rating?: number | null;
  status: string;
  submitted_at?: string | null;
  acknowledged_at?: string | null;
}

export interface PerformanceGoalFormState {
  employee_id: string;
  cycle_id: string;
  title: string;
  metric: string;
  target_value: string;
  weight: string | number;
  due_date: string;
  description: string;
}

export interface PerformanceReviewFormState {
  employee_id: string;
  cycle_id: string;
  overall_rating: string | number;
  manager_comments: string;
}

export interface CompensationCycle {
  id: number | string;
  name: string;
  effective_date: string;
  status: string;
}

export interface ManagerCompensationAdjustment {
  id: number | string;
  employee_name: string;
  employee_code: string;
  cycle_name?: string | null;
  current_basic_salary: number;
  proposed_basic_salary: number;
  merit_percent: number;
  total_first_year_cost: number;
  rationale?: string | null;
  status: string;
  decision_reason?: string | null;
}

export interface CompensationAdjustmentFormState {
  employee_id: string;
  cycle_id: string;
  proposed_basic_salary: string | number;
  proposed_taxable_allowance: string | number;
  lump_sum_bonus: string | number;
  rationale: string;
}

export interface InboxPageProps {
  requests: ManagerLeaveRequest[];
  onDecide: (requestId: number, decision: LeaveDecision) => void | Promise<void>;
  teamSummary?: ManagerTeamSummary | null;
  timesheets: ManagerTimesheet[];
  onTimesheetDecide: (timesheetId: number, decision: LeaveDecision) => void | Promise<void>;
  teamMembers: ManagerEmployee[];
  performanceCycles: PerformanceCycle[];
  performanceGoals: ManagerPerformanceGoal[];
  performanceReviews: ManagerPerformanceReview[];
  performanceGoalForm: PerformanceGoalFormState;
  setPerformanceGoalForm: Dispatch<SetStateAction<PerformanceGoalFormState>>;
  performanceReviewForm: PerformanceReviewFormState;
  setPerformanceReviewForm: Dispatch<SetStateAction<PerformanceReviewFormState>>;
  onPerformanceGoalSubmit: FormEventHandler<HTMLFormElement>;
  onPerformanceReviewSubmit: FormEventHandler<HTMLFormElement>;
  isPerformanceSubmitting: boolean;
  compensationCycles: CompensationCycle[];
  compensationAdjustments: ManagerCompensationAdjustment[];
  compensationAdjustmentForm: CompensationAdjustmentFormState;
  setCompensationAdjustmentForm: Dispatch<SetStateAction<CompensationAdjustmentFormState>>;
  onCompensationAdjustmentSubmit: FormEventHandler<HTMLFormElement>;
  isCompensationSubmitting: boolean;
}

const InboxPage: FC<InboxPageProps> = ({
  requests,
  onDecide,
  teamSummary,
  timesheets,
  onTimesheetDecide,
  teamMembers,
  performanceCycles,
  performanceGoals,
  performanceReviews,
  performanceGoalForm,
  setPerformanceGoalForm,
  performanceReviewForm,
  setPerformanceReviewForm,
  onPerformanceGoalSubmit,
  onPerformanceReviewSubmit,
  isPerformanceSubmitting,
  compensationCycles,
  compensationAdjustments,
  compensationAdjustmentForm,
  setCompensationAdjustmentForm,
  onCompensationAdjustmentSubmit,
  isCompensationSubmitting,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <article className="card list-card">
        <h2>Team Operations</h2>
        {teamSummary ? (
          <dl>
            <div><dt>Headcount</dt><dd>{teamSummary.headcount}</dd></div>
            <div><dt>Pending leave</dt><dd>{teamSummary.pending_leave_requests}</dd></div>
            <div><dt>Pending attendance</dt><dd>{teamSummary.pending_timesheets}</dd></div>
            <div><dt>Open clock sessions</dt><dd>{teamSummary.open_clock_sessions}</dd></div>
            <div><dt>Approved in 30 days</dt><dd>{teamSummary.approved_timesheets_30d}</dd></div>
            <div><dt>Active goals</dt><dd>{teamSummary.active_performance_goals || 0}</dd></div>
            <div><dt>Submitted reviews</dt><dd>{teamSummary.submitted_performance_reviews || 0}</dd></div>
            <div><dt>Pending compensation</dt><dd>{teamSummary.pending_compensation_adjustments || 0}</dd></div>
          </dl>
        ) : (
          <p>No team summary available yet.</p>
        )}
      </article>

      <article className="card list-card">
        <h2>{t('managerApprovalQueue')}</h2>
        {requests.length ? (
          <ul className="list">
            {requests.map((leaveRequest) => (
              <li key={leaveRequest.id}>
                <div>
                  <strong>{leaveRequest.employee_name}</strong>
                  <span>
                    {leaveRequest.leave_type_name} • {leaveRequest.start_date} to {leaveRequest.end_date} •{' '}
                    {leaveRequest.status}
                  </span>
                </div>
                {leaveRequest.status === 'pending' ? (
                  <div className="inline-actions">
                    <button type="button" className="secondary-button" onClick={() => void onDecide(leaveRequest.id, 'approve')}>
                      {t('approve')}
                    </button>
                    <button type="button" className="secondary-button" onClick={() => void onDecide(leaveRequest.id, 'reject')}>
                      {t('reject')}
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p>{t('noLeaveRequests')}</p>
        )}
      </article>

      <article className="card list-card">
        <h2>Attendance Approval Queue</h2>
        {timesheets.length ? (
          <ul className="list">
            {timesheets.map((timesheet) => (
              <li key={timesheet.id}>
                <div>
                  <strong>{timesheet.employee_name}</strong>
                  <span>
                    {timesheet.employee_code} • {timesheet.department || 'General'} • {timesheet.work_date} •{' '}
                    {Number(timesheet.regular_hours).toFixed(2)}h regular • {Number(timesheet.overtime_hours).toFixed(2)}h OT • {timesheet.status}
                  </span>
                </div>
                {timesheet.status === 'pending' && timesheet.clock_out ? (
                  <div className="inline-actions">
                    <button type="button" className="secondary-button" onClick={() => void onTimesheetDecide(timesheet.id, 'approve')}>
                      {t('approve')}
                    </button>
                    <button type="button" className="secondary-button" onClick={() => void onTimesheetDecide(timesheet.id, 'reject')}>
                      {t('reject')}
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p>No attendance records are waiting for review.</p>
        )}
      </article>

      <article className="card list-card">
        <h2>Performance Goals</h2>
        <form className="subcard" onSubmit={onPerformanceGoalSubmit}>
          <div className="split">
            <label>
              Employee
              <select
                value={performanceGoalForm.employee_id}
                onChange={(event) => setPerformanceGoalForm((current) => ({ ...current, employee_id: event.target.value }))}
              >
                <option value="">Select employee</option>
                {teamMembers.map((employee) => (
                  <option key={employee.id} value={employee.id}>{employee.full_name} ({employee.employee_code})</option>
                ))}
              </select>
            </label>
            <label>
              Cycle
              <select
                value={performanceGoalForm.cycle_id}
                onChange={(event) => setPerformanceGoalForm((current) => ({ ...current, cycle_id: event.target.value }))}
              >
                <option value="">No cycle</option>
                {performanceCycles.map((cycle) => (
                  <option key={cycle.id} value={cycle.id}>{cycle.name}</option>
                ))}
              </select>
            </label>
          </div>
          <label>
            Goal title
            <input
              value={performanceGoalForm.title}
              onChange={(event) => setPerformanceGoalForm((current) => ({ ...current, title: event.target.value }))}
            />
          </label>
          <div className="split">
            <label>
              Metric
              <input
                value={performanceGoalForm.metric}
                onChange={(event) => setPerformanceGoalForm((current) => ({ ...current, metric: event.target.value }))}
              />
            </label>
            <label>
              Target
              <input
                value={performanceGoalForm.target_value}
                onChange={(event) => setPerformanceGoalForm((current) => ({ ...current, target_value: event.target.value }))}
              />
            </label>
          </div>
          <div className="split">
            <label>
              Weight
              <input
                type="number"
                min="0"
                max="100"
                value={performanceGoalForm.weight}
                onChange={(event) => setPerformanceGoalForm((current) => ({ ...current, weight: event.target.value }))}
              />
            </label>
            <label>
              Due date
              <input
                value={performanceGoalForm.due_date}
                onChange={(event) => setPerformanceGoalForm((current) => ({ ...current, due_date: event.target.value }))}
              />
            </label>
          </div>
          <label>
            Description
            <textarea
              value={performanceGoalForm.description}
              onChange={(event) => setPerformanceGoalForm((current) => ({ ...current, description: event.target.value }))}
            />
          </label>
          <button type="submit" disabled={isPerformanceSubmitting}>{isPerformanceSubmitting ? 'Saving...' : 'Assign goal'}</button>
        </form>
        {performanceGoals.length ? (
          <ul className="list compact-list">
            {performanceGoals.map((goal) => (
              <li key={goal.id}>
                <div>
                  <strong>{goal.employee_name}</strong>
                  <span>
                    {goal.title} • {goal.cycle_name || 'No cycle'} • {goal.metric || 'Goal'} {goal.target_value ? `target ${goal.target_value}` : ''} •{' '}
                    {Number(goal.progress).toFixed(0)}% • {goal.status}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p>No performance goals assigned yet.</p>
        )}
      </article>

      <article className="card list-card">
        <h2>Performance Reviews</h2>
        <form className="subcard" onSubmit={onPerformanceReviewSubmit}>
          <div className="split">
            <label>
              Employee
              <select
                value={performanceReviewForm.employee_id}
                onChange={(event) => setPerformanceReviewForm((current) => ({ ...current, employee_id: event.target.value }))}
              >
                <option value="">Select employee</option>
                {teamMembers.map((employee) => (
                  <option key={employee.id} value={employee.id}>{employee.full_name} ({employee.employee_code})</option>
                ))}
              </select>
            </label>
            <label>
              Cycle
              <select
                value={performanceReviewForm.cycle_id}
                onChange={(event) => setPerformanceReviewForm((current) => ({ ...current, cycle_id: event.target.value }))}
              >
                <option value="">Select cycle</option>
                {performanceCycles.map((cycle) => (
                  <option key={cycle.id} value={cycle.id}>{cycle.name}</option>
                ))}
              </select>
            </label>
          </div>
          <label>
            Rating
            <input
              type="number"
              min="1"
              max="5"
              step="0.1"
              value={performanceReviewForm.overall_rating}
              onChange={(event) => setPerformanceReviewForm((current) => ({ ...current, overall_rating: event.target.value }))}
            />
          </label>
          <label>
            Manager comments
            <textarea
              value={performanceReviewForm.manager_comments}
              onChange={(event) => setPerformanceReviewForm((current) => ({ ...current, manager_comments: event.target.value }))}
            />
          </label>
          <button type="submit" disabled={isPerformanceSubmitting}>{isPerformanceSubmitting ? 'Saving...' : 'Submit review'}</button>
        </form>
        {performanceReviews.length ? (
          <ul className="list compact-list">
            {performanceReviews.map((review) => (
              <li key={review.id}>
                <div>
                  <strong>{review.employee_name}</strong>
                  <span>
                    {review.cycle_name} • rating {review.overall_rating ?? 'not rated'} • {review.status}
                    {review.acknowledged_at ? ` • acknowledged ${review.acknowledged_at}` : ''}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p>No performance reviews submitted yet.</p>
        )}
      </article>

      <article className="card list-card">
        <h2>Compensation Proposals</h2>
        <form className="subcard" onSubmit={onCompensationAdjustmentSubmit}>
          <div className="split">
            <label>
              Employee
              <select
                value={compensationAdjustmentForm.employee_id}
                onChange={(event) => setCompensationAdjustmentForm((current) => ({ ...current, employee_id: event.target.value }))}
              >
                <option value="">Select employee</option>
                {teamMembers.map((employee) => (
                  <option key={employee.id} value={employee.id}>{employee.full_name} ({employee.employee_code})</option>
                ))}
              </select>
            </label>
            <label>
              Cycle
              <select
                value={compensationAdjustmentForm.cycle_id}
                onChange={(event) => setCompensationAdjustmentForm((current) => ({ ...current, cycle_id: event.target.value }))}
              >
                <option value="">No cycle</option>
                {compensationCycles.map((cycle) => (
                  <option key={cycle.id} value={cycle.id}>{cycle.name}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="split split-three">
            <label>
              New basic salary
              <input
                type="number"
                min="1"
                step="0.01"
                value={compensationAdjustmentForm.proposed_basic_salary}
                onChange={(event) => setCompensationAdjustmentForm((current) => ({ ...current, proposed_basic_salary: event.target.value }))}
              />
            </label>
            <label>
              New taxable allowance
              <input
                type="number"
                min="0"
                step="0.01"
                value={compensationAdjustmentForm.proposed_taxable_allowance}
                onChange={(event) => setCompensationAdjustmentForm((current) => ({ ...current, proposed_taxable_allowance: event.target.value }))}
              />
            </label>
            <label>
              Lump sum bonus
              <input
                type="number"
                min="0"
                step="0.01"
                value={compensationAdjustmentForm.lump_sum_bonus}
                onChange={(event) => setCompensationAdjustmentForm((current) => ({ ...current, lump_sum_bonus: event.target.value }))}
              />
            </label>
          </div>
          <label>
            Rationale
            <textarea
              value={compensationAdjustmentForm.rationale}
              onChange={(event) => setCompensationAdjustmentForm((current) => ({ ...current, rationale: event.target.value }))}
            />
          </label>
          <button type="submit" disabled={isCompensationSubmitting}>{isCompensationSubmitting ? 'Saving...' : 'Submit proposal'}</button>
        </form>
        {compensationAdjustments.length ? (
          <ul className="list compact-list">
            {compensationAdjustments.map((adjustment) => (
              <li key={adjustment.id}>
                <div>
                  <strong>{adjustment.employee_name}</strong>
                  <span>
                    {adjustment.employee_code} • {adjustment.cycle_name || 'No cycle'} • {adjustment.current_basic_salary} to {adjustment.proposed_basic_salary} •{' '}
                    {Number(adjustment.merit_percent).toFixed(2)}% • first-year cost {adjustment.total_first_year_cost} • {adjustment.status}
                  </span>
                  {adjustment.rationale ? <span>{adjustment.rationale}</span> : null}
                  {adjustment.decision_reason ? <span>Decision: {adjustment.decision_reason}</span> : null}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p>No compensation proposals submitted yet.</p>
        )}
      </article>
    </>
  );
};

export default InboxPage;
